import groupBy from 'lodash-es/groupBy';
import remove from 'lodash-es/remove';
import { v4 as uuidv4 } from 'uuid';
import type { ShopperBasketsTypes } from 'commerce-sdk-isomorphic';
import { ShippingOptionType } from '../bff-api/baskets/model';
import { ProductType } from '../bff-api/baskets/model/ProductType';
import { isOfflineProduct } from '../bff-api/products/model';
import { PageUrl } from '../pages/utils/page-url';
import type { ShippingOption } from '../bff-api/baskets/model';
import type {
    CulinaryProductItem,
    ElectronicGiftCardProductItem,
    ExtendPostPurchaseProductId,
    ExtendPostPurchaseProductItem,
    ExtendProductId,
    ExtendProductItem,
    GiftRegistryProductItem,
    HardGoodItem,
    NewProductItem,
    PhysicalGiftCardProductItem,
    ProductItem,
    ProductItems,
} from '../bff-api/baskets/model/ProductItem';
import type { Product } from '../bff-api/products/model';
import type { ProductItemsCounts } from '../contexts/model/CartContext';
import type { CartCulinaryProductItem } from '../pages/cart/models/CartCulinaryProductItem';
import type { Dictionary } from '../types/helpers';
import type {
    BackorderShipment,
    DropshipShipment,
    RegularShipment,
    SameDayShipment,
    Shipments,
} from './api/Shipment';

const EXTEND_ID_PREFIX = 'EXTEND-';
const EXTEND_POST_PURCHASE_ID_PREFIX = 'EXTENDPP-';

const addAssociatedExtendProductItems = <T extends ProductItem>(
    filteredProductItems: T[],
    products: T[],
): T[] => {
    let filteredIds = filteredProductItems.map((p) =>
        isExtend(p) ? `${p.productId}-${p.c_ExtendAssociation}` : p.productId,
    );

    let newProductItems = filteredProductItems;

    products.forEach((product) => {
        if (isExtend(product)) {
            const hasAssociatedProduct = filteredIds.includes(product.c_ExtendAssociation ?? '');
            const alreadyAdded = filteredIds.includes(
                `${product.productId}-${product.c_ExtendAssociation}`,
            );

            if (hasAssociatedProduct && !alreadyAdded) {
                // Add the Extend product just after its associated Product.
                const index = filteredIds.findIndex((id) => id === product.c_ExtendAssociation) + 1;
                newProductItems = [
                    ...newProductItems.slice(0, index),
                    product,
                    ...newProductItems.slice(index),
                ];

                filteredIds = newProductItems.map((p) => p.productId);
            }
        }
    });

    return newProductItems;
};

const removeNonAssociatedExtendProductItems = <T extends ProductItem>(
    filteredProductItems: T[],
): T[] => {
    const filteredIds = filteredProductItems.map((product) => product.productId);

    // eslint-disable-next-line lodash/prefer-immutable-method
    remove(filteredProductItems, (product) => {
        if (isExtend(product) && !isExtendPostPurchase(product)) {
            return !filteredIds.includes(product.c_ExtendAssociation);
        }

        return false;
    });

    return filteredProductItems;
};

export function isExtendPostPurchaseProductId(productId: string): productId is ExtendProductId {
    return productId.startsWith(EXTEND_POST_PURCHASE_ID_PREFIX);
}

export function getExtendProductId(
    term: number,
    isPP: boolean,
): ExtendProductId | ExtendPostPurchaseProductId {
    if ([12, 24, 36].includes(term)) {
        return `EXTEND${isPP ? 'PP' : ''}-${term}`;
    }
    throw new Error('Invalid extend term');
}

export const isExtend = (product: ProductItem): product is ExtendProductItem =>
    product.productId.startsWith(EXTEND_ID_PREFIX) &&
    'c_ExtendAssociation' in product &&
    'c_parentLineItemUUID' in product;

export const isExtendPostPurchase = (
    product: ProductItem,
): product is ExtendPostPurchaseProductItem =>
    product.productId.startsWith(EXTEND_POST_PURCHASE_ID_PREFIX) &&
    'c_leadQuantity' in product &&
    !!product.c_postPurchaseLeadToken;

export const isShippingProtection = (product: ProductItem): boolean =>
    product.productId === 'SHIPPING_PROTECTION';

export function isElectronicGiftCard(
    product: ProductItem | Product,
): product is ElectronicGiftCardProductItem {
    return product.c_productType === ProductType.GIFT_CARD_ELECTRONIC;
}

export function isPhysicalGiftCard(
    product: ProductItem | Product,
): product is PhysicalGiftCardProductItem {
    return product.c_productType === ProductType.GIFT_CARD_PHYSICAL;
}

export function isGiftCard(product: ProductItem | Product): boolean {
    return isElectronicGiftCard(product) || isPhysicalGiftCard(product);
}

export function isCulinary(product: ProductItem): product is CulinaryProductItem {
    return 'c_culinaryClass' in product && product.c_productType === ProductType.CULINARY;
}

export function isHardGood(param: ProductItem): param is HardGoodItem {
    return param.c_productType === ProductType.HARD_GOOD;
}

export function isShippableBySLT(product: ProductItem): boolean {
    return (
        (isHardGood(product) || isPhysicalGiftCard(product) || isShippingProtection(product)) &&
        !isBopis(product) &&
        !isSDD(product) &&
        !isDropship(product) &&
        !isExtend(product) &&
        !isExtendPostPurchase(product)
    );
}

export function isShippable(product: ProductItem): boolean {
    return isShippableBySLT(product) || isDropship(product) || !!product.c_isSameDayDelivery;
}

/**
 * Similar to isShippableBySLT but excludes shipping protection items
 * Used to if products are in cart are eligible for shipping protection
 */
export function isShippableProductForProtection(product: ProductItem): boolean {
    return (
        isHardGood(product) &&
        !isBopis(product) &&
        !isSDD(product) &&
        !isExtend(product) &&
        !isExtendPostPurchase(product) &&
        !isShippingProtection(product)
    );
}

export function isShiptToCartSection(product: ProductItem): boolean {
    return (
        (isHardGood(product) || isPhysicalGiftCard(product)) &&
        !isBopis(product) &&
        !isSDD(product) &&
        !isExtend(product) &&
        !isExtendPostPurchase(product)
    );
}

export function isExperience(product: ProductItem): boolean {
    return isCulinary(product) || isElectronicGiftCard(product) || isExtendPostPurchase(product);
}

export function isGiftRegistry(product: ProductItem): product is GiftRegistryProductItem {
    return !!product.c_registrant && !!product.c_GiftRegistryLineItem;
}

const sortShippableProductItems = (p1: ProductItem, p2: ProductItem): number => {
    // First backorder
    if (p1.c_onBackorder && !p2.c_onBackorder) {
        return -1;
    }

    // Second Physical Gift Card
    if (isPhysicalGiftCard(p1) && !isPhysicalGiftCard(p2) && !p2.c_onBackorder) {
        return -1;
    }

    // Third HardGood with Warranty and the Warranty
    if (p1.c_ExtendAssociation && isExtend(p2) && !isPhysicalGiftCard(p2) && !p2.c_onBackorder) {
        // If p1 is the Extend of p2, put p2 first, otherwise p1 first.
        return isExtend(p1) && p1.c_ExtendAssociation === p2.productId ? 1 : -1;
    }

    return 0;
};

const sortProductItems = (p1: ProductItem, p2: ProductItem): number => {
    // First Gift Registry
    if (p1.c_GiftRegistryLineItem) {
        return -1;
    }

    // Second DropShip without Extend
    if (!p1.c_ExtendAssociation && !p2.c_GiftRegistryLineItem) {
        return -1;
    }

    return 0;
};

export const getShippableProductItems = <T extends ProductItem>(products: T[] = []): T[] => {
    if (!products.length) {
        return [];
    }

    let filteredProductItems = products.filter(isShippableBySLT).sort(sortShippableProductItems);

    filteredProductItems = addAssociatedExtendProductItems(filteredProductItems, products);

    filteredProductItems = removeNonAssociatedExtendProductItems(filteredProductItems);

    return filteredProductItems;
};

// Separate method required to keep original product order in the cart and do not separate shipping from backorder items
export const getShipItProducts = <T extends ProductItem>(products: T[] = []): T[] => {
    if (!products.length) {
        return [];
    }

    let filteredProductItems = products.filter(isShiptToCartSection);
    filteredProductItems = addAssociatedExtendProductItems(filteredProductItems, products);

    filteredProductItems = removeNonAssociatedExtendProductItems(filteredProductItems);

    return filteredProductItems;
};

export const hasCulinaryProductItems = (products: ProductItems): boolean =>
    products.some(isCulinary);

export const getBopisProductItems = <T extends ProductItem>(products: T[] = []): T[] =>
    products.filter(isBopis);

export const getSameDayDeliveryProductItems = <T extends ProductItem>(products: T[] = []): T[] =>
    products.filter(isSDD);

export const getGiftRegistryProductItems = <T extends ProductItem>(products: T[] = []): T[] =>
    products.filter(({ c_GiftRegistryLineItem }) => c_GiftRegistryLineItem);

export const hasBopisProductItems = (products: ProductItems = []): boolean =>
    products.some(isBopis);

export const getCulinaryProductItems = (products: ProductItems = []): CartCulinaryProductItem[] =>
    products.filter(isCulinary).map((culinaryProduct) => ({
        ...culinaryProduct,
        c_culinaryClass: {
            ...culinaryProduct.c_culinaryClass,
            attendees: [
                ...(culinaryProduct?.c_culinaryClass?.attendees ?? []).map((attendee) => ({
                    ...attendee,
                    id: uuidv4(),
                })),
            ],
        },
    }));

export const getEGiftCardProductItems = <T extends ProductItem>(products: T[] = []): T[] =>
    products.filter(isElectronicGiftCard);

export const getPhysicalGiftCardProductItems = <T extends ProductItem>(products: T[] = []): T[] =>
    products.filter(isPhysicalGiftCard);

export const getPostPurchaseWarrantyProductItems = <T extends ProductItem>(
    products: T[] = [],
): T[] => products.filter(isExtendPostPurchase);

export const getShippingProtectionItems = <T extends ProductItem>(products: T[] = []): T[] =>
    products.filter(isShippingProtection);

export const getDropShipMessage = ({
    c_dropshipEstimatedDeliveryDate,
    c_dropshipEstimatedProcessingDate,
}: Pick<
    ProductItem,
    'c_dropshipEstimatedDeliveryDate' | 'c_dropshipEstimatedProcessingDate'
>): string => {
    const DEFAULT_MESSAGE = 'Our vendor will ship directly to you';
    if (
        c_dropshipEstimatedDeliveryDate === DEFAULT_MESSAGE ||
        c_dropshipEstimatedProcessingDate === DEFAULT_MESSAGE
    ) {
        return DEFAULT_MESSAGE;
    }
    if (c_dropshipEstimatedDeliveryDate) {
        if (c_dropshipEstimatedDeliveryDate && c_dropshipEstimatedDeliveryDate.includes('days')) {
            return c_dropshipEstimatedDeliveryDate.replace(' days', ' business days');
        }
        return `Allow ${c_dropshipEstimatedDeliveryDate} for delivery`;
    }
    if (c_dropshipEstimatedProcessingDate) {
        return `Allow ${c_dropshipEstimatedProcessingDate} to ship`;
    }
    return DEFAULT_MESSAGE;
};

export const getDropShipProductItems = <T extends ProductItem>(products: T[] = []): T[] => {
    if (!products.length) {
        return [];
    }

    let filteredProductItems = products.filter(isDropship).sort(sortProductItems);

    filteredProductItems = addAssociatedExtendProductItems(filteredProductItems, products);

    filteredProductItems = removeNonAssociatedExtendProductItems(filteredProductItems);

    return filteredProductItems;
};

export const getDropShipProductItemsDictionary = (
    products: ProductItems,
): Dictionary<ProductItems> => {
    if (!products.length) {
        return {};
    }

    const nonExtendProductItems = products.filter((p) => !isExtend(p) || isExtendPostPurchase(p));
    const extendProductItems = products.filter(isExtend);
    const nonExtendDictionary = groupBy(nonExtendProductItems, getDropShipMessage);
    const dictionary: Dictionary<ProductItems> = {};

    Object.keys(nonExtendDictionary).forEach((key) => {
        let filteredProductItems = addAssociatedExtendProductItems(
            nonExtendDictionary[key],
            extendProductItems,
        );
        filteredProductItems = removeNonAssociatedExtendProductItems(filteredProductItems);

        dictionary[key] = filteredProductItems;
    });

    return dictionary;
};

export const isBackorder = ({
    c_onBackorder,
    c_isSameDayDelivery,
    c_storepickupenabled,
}: ProductItem): boolean => Boolean(c_onBackorder) && !c_isSameDayDelivery && !c_storepickupenabled;

export const getBackorderProductItems = <T extends ProductItem>(products: T[] = []): T[] => {
    if (!products.length) {
        return [];
    }

    let filteredProductItems = products.filter(isBackorder).sort(sortProductItems);

    filteredProductItems = addAssociatedExtendProductItems(filteredProductItems, products);

    filteredProductItems = removeNonAssociatedExtendProductItems(filteredProductItems);

    return filteredProductItems;
};

export const getBackorderProductItemsDictionary = (
    products: ProductItems,
): Dictionary<ProductItems> => {
    if (!products.length) {
        return {};
    }

    const nonExtendProductItems = products.filter((p) => !isExtend(p) || isExtendPostPurchase(p));
    const extendProductItems = products.filter(isExtend);
    const nonExtendDictionary = groupBy(nonExtendProductItems, 'c_inStockDate');
    const dictionary: Dictionary<ProductItems> = {};

    Object.keys(nonExtendDictionary).forEach((key) => {
        let filteredProductItems = addAssociatedExtendProductItems(
            nonExtendDictionary[key],
            extendProductItems,
        );
        filteredProductItems = removeNonAssociatedExtendProductItems(filteredProductItems);

        dictionary[key] = filteredProductItems;
    });

    return dictionary;
};

export const getExperienceProductItems = <T extends ProductItem>(products: T[] = []): T[] => {
    if (!products.length) {
        return [];
    }

    const filteredProductItems = products.filter(isExperience);

    addAssociatedExtendProductItems(filteredProductItems, products);

    removeNonAssociatedExtendProductItems(filteredProductItems);

    return filteredProductItems;
};

export const hasGiftCardProductItems = (products: ProductItems): boolean =>
    products.some(isGiftCard);

export const hasDropShipProductItems = (products: ProductItems): boolean =>
    products.some(isDropship);

export const getProductItemsCounts = (products: ProductItems = []): ProductItemsCounts => {
    const counts = {
        countTotalProductItems:
            products.reduce(
                (acc, item: ShopperBasketsTypes.ProductItem) => acc + (item.quantity ?? 1),
                0,
            ) ?? 0,
        countHardGood: 0,
        countBopis: 0,
        countSameDayDelivery: 0,
        countExtendPostPurchase: 0,
        countBackorder: 0,
        countDropShip: 0,
        countElectronicGiftCard: 0,
        countPhysicalGiftCard: 0,
        countGiftCard: 0,
        countCulinary: 0,
        countShippable: 0,
        countShippableBySLT: 0,
        countExperience: 0,
        countGiftRegistry: 0,
        countShippingProtectionEligible: 0,
    };

    products.forEach((product) => {
        if (isHardGood(product)) counts.countHardGood += 1;
        if (isBopis(product)) counts.countBopis += 1;
        if (isSDD(product)) counts.countSameDayDelivery += 1;
        if (isExtendPostPurchase(product)) counts.countExtendPostPurchase += 1;
        if (isDropship(product)) counts.countDropShip += 1;
        if (isElectronicGiftCard(product)) counts.countElectronicGiftCard += 1;
        if (isPhysicalGiftCard(product)) counts.countPhysicalGiftCard += 1;
        if (isGiftCard(product)) counts.countGiftCard += 1;
        if (isCulinary(product)) counts.countCulinary += 1;
        if (isExperience(product)) counts.countExperience += 1;
        if (isBackorder(product)) counts.countBackorder += 1;
        const shippableBySLT = isShippableBySLT(product);
        if (shippableBySLT || isDropship(product) || product.c_isSameDayDelivery)
            counts.countShippable += 1;
        if (shippableBySLT) counts.countShippableBySLT += 1;
        if (isGiftRegistry(product)) counts.countGiftRegistry += 1;
        if (isShippableProductForProtection(product)) counts.countShippingProtectionEligible += 1;
    });

    return counts;
};

export const buildShipments = (
    shippingProductItems: ProductItems,
    sameDayDeliveryProductItems: ProductItems,
    dropShipProductItemsDictionary: Dictionary<ProductItems>,
    backorderProductItemsDictionary: Dictionary<ProductItems>,
): Shipments => {
    const backorderShipments = Object.keys(backorderProductItemsDictionary).map(
        (key): BackorderShipment => ({
            type: 'backorder',
            inStockDate: key,
            products: backorderProductItemsDictionary[key],
        }),
    );
    const dropshipShipments = Object.keys(dropShipProductItemsDictionary).map(
        (key): DropshipShipment => ({
            type: 'dropship',
            deliveryMessage: key,
            products: dropShipProductItemsDictionary[key],
        }),
    );
    const sameDayShipments: SameDayShipment = {
        type: 'same-day',
        products: sameDayDeliveryProductItems,
    };
    const regularShipment: RegularShipment = {
        type: 'ship',
        products: shippingProductItems.filter(({ c_onBackorder }) => !c_onBackorder),
    };

    const shipments: Shipments = [
        regularShipment,
        sameDayShipments,
        ...backorderShipments,
        ...dropshipShipments,
    ]
        .filter(({ products }) => products.length)
        .map((s, idx) => ({ ...s, order: idx }));

    return shipments;
};

export const buildProductItemForBag = (
    {
        id,
        inventory,
        c_dropShipEligibility,
        c_gcPersonalMessage1,
        c_gcPersonalMessage2,
        c_gcPersonalMessage3,
        c_gcPersonalMessage4,
        giftMessage,
        c_gcRecipientEmail,
        c_gcRecipientName,
        c_gcSenderName,
        c_gcAmount,
        c_gcDeliveryDate,
        c_productType = ProductType.HARD_GOOD,
    }: Product,
    quantity: number,
    shippingOption: ShippingOptionType,
    storeId?: string,
    edd?: number,
    shipmentId?: string,
): NewProductItem => {
    const item: NewProductItem = { productId: id, quantity };

    item.c_productType = c_productType;
    item.c_storepickupenabled = shippingOption === ShippingOptionType.BOPIS;
    item.c_isSameDayDelivery = shippingOption === ShippingOptionType.SDD;
    item.c_isDropShip = !!c_dropShipEligibility;
    item.c_onBackorder = !c_dropShipEligibility && !!inventory?.backorderable;

    if (c_gcDeliveryDate) item.c_gcDeliveryDate = c_gcDeliveryDate;
    if (c_gcPersonalMessage1) item.c_gcPersonalMessage1 = c_gcPersonalMessage1;
    if (c_gcPersonalMessage2) item.c_gcPersonalMessage2 = c_gcPersonalMessage2;
    if (c_gcPersonalMessage3) item.c_gcPersonalMessage3 = c_gcPersonalMessage3;
    if (c_gcPersonalMessage4) item.c_gcPersonalMessage4 = c_gcPersonalMessage4;
    if (giftMessage) item.giftMessage = giftMessage;
    if (c_gcRecipientEmail) item.c_gcRecipientEmail = c_gcRecipientEmail;
    if (c_gcRecipientName) item.c_gcRecipientName = c_gcRecipientName;
    if (c_gcSenderName) item.c_gcSenderName = c_gcSenderName;
    if (c_gcAmount) item.c_gcAmount = c_gcAmount;

    if (shippingOption === ShippingOptionType.SHIP_TO) {
        item.c_hasEDD = Number.isInteger(edd);
        item.c_finalEDD = edd;
    } else {
        item.c_hasEDD = false;
        item.c_fromStoreId = storeId;
    }

    item.shipmentId = shipmentId;

    return item;
};

export const hasAssociatedExtendProductItem = (
    productItem: ProductItem,
    productItemsInBasket: ProductItem[],
): boolean =>
    productItemsInBasket.some(
        (pi) =>
            pi.c_parentLineItemUUID === productItem.itemId &&
            pi.c_ExtendAssociation === productItem.productId,
    );

export const getAssociatedExtendProductItem = <T extends ProductItem>(
    productItem: ProductItem,
    productItems: T[],
): T | undefined =>
    productItems.find(
        (pi) =>
            isExtend(pi) &&
            pi.c_parentLineItemUUID === productItem.itemId &&
            pi.c_ExtendAssociation === productItem.productId,
    );

export function isDropship(product: ProductItem): boolean {
    return !!product.c_isDropShip;
}

export const isOutOfStock = ({ c_shippingOptions }: Product): boolean =>
    !c_shippingOptions.some(({ enabled }) => enabled);

export const buildPDPUrl = (product: Pick<Product, 'c_pwaUrl' | 'c_pageURL' | 'id'>): string =>
    product.c_pwaUrl || `${PageUrl.PRODUCT}/${product.c_pageURL ?? ''}/${product.id}`;

/**
 * Scenarios that return true:
 *
 * - Gift registry + Ship it item
 * - Gift registry + SDD
 * - More than 1 Gift Registry
 */
export function isMixedBagWithOneAddress(products: ProductItem[]): boolean {
    if (products.length < 2) {
        return false;
    }

    const giftRegistryItems = products.filter((item) => isGiftRegistry(item));
    const giftRegistryQty = giftRegistryItems.length;

    if (giftRegistryQty === 0) {
        return false;
    }

    if (giftRegistryQty === 1) {
        const hasShipTo = products.some(isShippableBySLT);
        const hasSDD = products.some(({ c_isSameDayDelivery }) => !!c_isSameDayDelivery);

        return hasShipTo || hasSDD;
    }

    const uniqueAddresses = new Set(
        giftRegistryItems.map((item) => item.productListItem?.productList?.c_id),
    );
    const allSameAddress = uniqueAddresses.size === 1;

    return allSameAddress;
}

export const isBopis = ({ c_storepickupenabled }: ProductItem): boolean => !!c_storepickupenabled;

export const isSDD = ({ c_isSameDayDelivery }: ProductItem): boolean => !!c_isSameDayDelivery;

export const isShipTo = (item: ProductItem): boolean => !isBopis(item) && !isSDD(item);

export const isEligibleItemForFreeShipping = (
    item: ProductItem,
    productItems: ProductItems,
): boolean =>
    !!(isShippableBySLT(item) && !isPhysicalGiftCard(item)) ||
    isDropship(item) ||
    (isCulinary(item) && productItems.some(isHardGood));

export const hasEligibleItemsForFreeShipping = (items: ProductItems | undefined): boolean =>
    !!items?.some((item) => isEligibleItemForFreeShipping(item, items ?? []));

export const hasOfflineProducts = (products: Product[]): boolean => products.some(isOfflineProduct);

export const getShippingOptions = ({
    c_shippingOptions = [],
}: Product): {
    shipToOption: ShippingOption | undefined;
    bopisOption: ShippingOption | undefined;
    sddOption: ShippingOption | undefined;
} => {
    const shipToOption = c_shippingOptions.find(({ type }) => type === ShippingOptionType.SHIP_TO);
    const bopisOption = c_shippingOptions.find(({ type }) => type === ShippingOptionType.BOPIS);
    const sddOption = c_shippingOptions.find(({ type }) => type === ShippingOptionType.SDD);

    return { shipToOption, bopisOption, sddOption };
};

export const getGiftCardDesignName = (product: Product): string => {
    const designName =
        product.variationAttributes?.[0]?.values.find((attribute) => attribute.value === product.id)
            ?.name || '';

    return designName;
};
